import { useNavigate, useParams } from "@tanstack/solid-router";
import { Show, createEffect, createResource, createSignal } from "solid-js";
import { MeetupQr } from "~/components/MeetupQr";
import { ApiError, cancelMeetup, fetchMeetupMine, fetchMeetupNight, registerMeetup } from "~/lib/api";
import { doorQrPayload, isMeetupFull, isMeetupPast } from "~/lib/meetup";

export function MeetupDetailPage() {
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const slug = () => params().slug ?? "";

  const [night] = createResource(slug, (s) => fetchMeetupNight(s).then((r) => r.data));
  const [door, setDoor] = createSignal<{ qr: string; short: string; ics?: string } | null>(null);
  const [error, setError] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const [autoTried, setAutoTried] = createSignal(false);

  async function revealQr() {
    const mine = await fetchMeetupMine(slug());
    const qr = doorQrPayload(mine.data);
    if (mine.data.status === "going" && qr) {
      setDoor({ qr, short: mine.data.short_code ?? "", ics: mine.data.ics });
    }
    return mine.data;
  }

  async function go() {
    setBusy(true);
    setError("");
    try {
      const res = await registerMeetup(slug());
      const qr = doorQrPayload(res.data);
      if (qr) {
        setDoor({ qr, short: res.data.short_code ?? "", ics: res.data.ics });
      } else {
        await revealQr();
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function leave() {
    setBusy(true);
    setError("");
    try {
      await cancelMeetup(slug());
      setDoor(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  createEffect(() => {
    const n = night();
    if (!n || autoTried() || night.loading) return;
    setAutoTried(true);
    if (isMeetupPast(n) || isMeetupFull(n)) {
      if (n.mine?.status === "going") void revealQr().catch(() => {});
      return;
    }
    if (n.mine?.status === "cancelled") return;
    void (async () => {
      setBusy(true);
      try {
        if (n.mine?.status === "going") {
          await revealQr();
        } else {
          await go();
        }
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          await go();
        } else {
          setError((err as Error).message);
        }
      } finally {
        setBusy(false);
      }
    })();
  });

  const past = () => {
    const n = night();
    return n ? isMeetupPast(n) : false;
  };
  const full = () => {
    const n = night();
    return n ? isMeetupFull(n) : false;
  };

  return (
    <section class="card">
      <p class="status-chip">{night()?.city ?? "Meetup"}</p>
      <Show when={night.loading}>
        <p class="muted">Loading meetup…</p>
      </Show>
      <Show when={night.error}>
        <div class="error-box">{(night.error as Error).message}</div>
      </Show>
      <Show when={night()}>
        {(n) => (
          <>
            <h1>{n().title}</h1>
            <p class="muted">
              {new Date(n().starts_at).toLocaleString("en-AU", {
                weekday: "long",
                day: "numeric",
                month: "long",
                hour: "numeric",
                minute: "2-digit",
                timeZone: "UTC",
              })}
              {n().location ? ` · ${n().location}` : ""}
            </p>
            <Show when={typeof n().remaining === "number"}>
              <p class="muted">
                {n().remaining === 0 ? "This meetup is full" : `${n().remaining} spots left`}
              </p>
            </Show>
            <p class="muted">Free meetup registration — not a conference ticket.</p>
          </>
        )}
      </Show>

      <Show when={error()}>
        <div class="error-box">{error()}</div>
      </Show>
      <Show when={busy() && !door()}>
        <p class="muted">Getting your door QR…</p>
      </Show>

      <Show when={door()}>
        {(d) => (
          <div class="meetup-door">
            <MeetupQr token={d().qr} />
            <p class="meetup-short-code">{d().short}</p>
            <p class="muted">Show this at the door. Short code if the scan fails.</p>
          </div>
        )}
      </Show>

      <Show when={!past() && !full() && !door() && night()?.mine?.status === "cancelled"}>
        <button type="button" class="btn btn-primary" style={{ width: "100%" }} onClick={() => void go()} disabled={busy()}>
          Register again
        </button>
      </Show>
      <Show when={door() && !past()}>
        <button type="button" class="btn btn-outline" style={{ width: "100%", "margin-top": "12px" }} onClick={() => void leave()} disabled={busy()}>
          Cancel registration
        </button>
      </Show>
      <Show when={past() && !door()}>
        <p class="muted">This meetup has started.</p>
      </Show>
      <Show when={full() && !door() && night()?.mine?.status !== "going"}>
        <p class="muted">This meetup is full.</p>
      </Show>

      <button type="button" class="btn btn-ghost" style={{ width: "100%", "margin-top": "16px" }} onClick={() => navigate({ to: "/meetup" })}>
        All meetups
      </button>
    </section>
  );
}
